import { useFixtures, useMeta, useSession, type DemoFixture } from "@/api/queries";
import { Card, CardBody, CardFooter, CardHeader } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { DatabaseIcon, ExternalIcon } from "@/components/icons";
import { useBrand } from "@/theme/context";

/**
 * The deployment's own facts — version, runtime, theme and demo data.
 *
 * These were the front page while the platform was being built, and they are
 * an operator's reference rather than anybody's first screen. They live here
 * now, beside the limits and storage they explain; the front page is for
 * asking questions and reading the numbers.
 */
export function DeploymentFacts() {
  const { theme, categorical } = useBrand();
  const { data: meta } = useMeta();
  const { data: session } = useSession();

  return (
    <Card>
      <CardHeader title="Deployment" description="Runtime facts and the theme it is running" />
      <CardBody className="grid gap-6 sm:grid-cols-2">
        <dl className="flex flex-col gap-2">
          {[
            ["Version", meta ? `v${meta.version}` : "—"],
            ["Environment", meta?.environment ?? "—"],
            ["Timezone", meta?.timezone ?? "—"],
            ["Milestone", meta?.milestone.split("—")[0]?.trim() ?? "—"],
            [
              "Your session",
              session?.role
                ? `${session.role === "admin" ? "Admin" : "Analyst"}${session.method ? ` · ${session.method}` : ""}`
                : "—",
            ],
          ].map(([label, value]) => (
            <div key={label} className="flex items-baseline justify-between gap-4">
              <dt className="type-support text-ink-secondary">{label}</dt>
              <dd className="type-support text-ink">{value}</dd>
            </div>
          ))}
        </dl>
        <div>
          <div className="flex items-baseline gap-2">
            <span className="type-h4 text-ink tabular-nums">{theme?.version ?? "—"}</span>
            <span className="type-support text-ink-secondary">tokens.json</span>
          </div>
          <div className="mt-3 flex gap-1" aria-label="Categorical chart palette">
            {categorical.map((color, index) => (
              <span
                key={`${color}-${index}`}
                title={color}
                className="h-5 flex-1 rounded-inner"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          <p className="type-support text-ink-secondary mt-3">
            {theme?.brand.font.display.family} · {theme?.brand.font.body.family} ·{" "}
            {theme?.brand.font.mono.family}
          </p>
        </div>
      </CardBody>
      <CardFooter>
        <a
          href="/api/docs"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-accent hover:underline"
        >
          Browse the API
          <ExternalIcon className="size-3.5" />
        </a>
      </CardFooter>
    </Card>
  );
}

function RelationSummary({ fixture }: { fixture: DemoFixture }) {
  const tables = fixture.relations.filter((relation) => relation.kind === "table");
  const views = fixture.relations.filter((relation) => relation.kind === "view");
  const rows = fixture.relations.reduce((total, relation) => total + (relation.row_count ?? 0), 0);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 type-support text-ink-secondary">
      <span>{tables.length} tables</span>
      <span>{views.length} views</span>
      <span className="text-ink tabular-nums">{rows.toLocaleString()} rows</span>
    </div>
  );
}

export function DemoData() {
  const { data: fixtureList } = useFixtures(true);
  const fixtures = fixtureList?.fixtures ?? [];
  return (
    <Card>
      <CardHeader
        title="Demo data"
        description="Bundled synthetic databases, installed in the data volume"
        action={
          <span className="text-ink-disabled">
            <DatabaseIcon className="size-5" />
          </span>
        }
      />
      <CardBody className="grid gap-4 lg:grid-cols-2">
        {fixtures.length === 0 ? (
          <p className="type-support text-ink-secondary">No fixtures found.</p>
        ) : (
          fixtures.map((fixture) => (
            <div key={fixture.slug} className="rounded-element border border-border p-4">
              <div className="flex items-center gap-2">
                <span className="type-h4 text-ink">{fixture.name}</span>
                <Chip tone={fixture.installed ? "success" : "warning"}>
                  {fixture.installed ? "installed" : "bundled"}
                </Chip>
                {fixture.primary ? <Chip tone="accent">primary</Chip> : null}
              </div>
              <p className="type-support text-ink-secondary mt-2">{fixture.summary}</p>
              <div className="mt-3">
                <RelationSummary fixture={fixture} />
              </div>
            </div>
          ))
        )}
      </CardBody>
    </Card>
  );
}
